import { ChecklistSubitem, ItemChecklist, ocorrenciaType, SubitemChecklist } from "../../../types/checklist/types";
import { ChecklistReportType } from "../../../types/reports/types";
import { ExportRow } from "../../../types/reports/types";

const CSV_HEADERS: (keyof ExportRow)[] = [
  "Início",
  "Fim",
  "Sincronizado",
  "Formulário",
  "Score",
  "Usuário",
  "Gerência",
  "Cliente",
  "Local",
  "Conjunto",
  "Unidade",
  "Item de Recorrência",
  "Item",
  "Subitem",
  "Resposta",
  "Comentário",
  "Não Conformidades",
  "Conformidades",
];

const formatDate = (value?: string | number): string => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.toLocaleDateString("pt-BR")} ${date.toLocaleTimeString("pt-BR")}`;
};

const getSelectedOptions = (sub: ChecklistSubitem): string[] => {
  if (!Array.isArray(sub.selecao) || !Array.isArray(sub.selecionados)) {
    return [];
  }

  return sub.selecao.filter((_, index) => Boolean(sub.selecionados?.[index])).map((option) => String(option));
};

const getResposta = (sub: ChecklistSubitem): string => {
  switch (sub.tipo) {
    case "multiple": {
      return getSelectedOptions(sub).join(" | ");
    }
    case "unique": {
      return getSelectedOptions(sub)[0] ?? "";
    }
    case "list": {
      if (!Array.isArray(sub.selecionados)) {
        return "";
      }

      return sub.selecionados
        .map((value) => {
          if (typeof value === "object" && value && "texto" in value) {
            return String((value as { texto: string }).texto);
          }
          return String(value);
        })
        .join(" | ");
    }
    case "avaliacao": {
      if (!Array.isArray(sub.avaliacao) || typeof sub.nota !== "number") {
        return "";
      }

      const selected = sub.nota === -1 ? sub.avaliacao[sub.avaliacao.length - 1] : sub.avaliacao[sub.nota];
      if (!selected) {
        return "";
      }

      return `${selected.valor} - ${selected.nome}`;
    }
    case "data": {
      if (typeof sub.ocorrencia === "number") {
        return new Date(sub.ocorrencia).toLocaleDateString("pt-BR");
      }
      return String(sub.ocorrencia ?? "");
    }
    case "assinatura": {
      return sub.assinatura ? "Assinatura preenchida" : "";
    }
    case "valorMedida": {
      const subtipo = String(sub.subtipo ?? "Valor/Medida");
      const valor = String(sub.ocorrencia ?? "");
      return valor ? `${subtipo} | ${valor}` : "";
    }
    default:
      return String(sub.ocorrencia ?? "");
  }
};

const getComentario = (sub: ChecklistSubitem): string => {
  switch (sub.tipo) {
    case "textoCurto":
    case "textoLongo":
      return "";
    case "multiple":
    case "avaliacao":
      return String(sub.ocorrencia ?? "");
    case "unique":
    case "list":
    case "valorMedida":
    default:
      return String(sub.obs ?? "");
  }
};

const findOccurrenceName = (item: ItemChecklist, sub: ChecklistSubitem): string => {
  if (!item.ocorrenciaMultipla || !item.ocorrencias?.length) {
    return "";
  }

  const byReference = item.ocorrencias.find((occ) => occ.subitens?.includes(sub as any));
  if (byReference?.nome) {
    return byReference.nome;
  }

  const byFieldMatch = item.ocorrencias.find((occ) =>
    occ.subitens?.some((occSub) => occSub.id === sub.id && occSub.nome === sub.nome && occSub.itemid === sub.itemid),
  );

  return byFieldMatch?.nome ?? "";
};

const buildRow = (form: ChecklistReportType, item: ItemChecklist, sub: ChecklistSubitem, occurrenceName: string): ExportRow => {
  const user = form.user ?? form.usuario ?? form.userName ?? form.username ?? "";

  return {
    "Início": formatDate(form.inicio),
    Fim: formatDate(form.fim),
    Sincronizado: formatDate((form as ChecklistReportType & { dataEnvio?: number | string }).dataEnvio),
    Formulário: String(form.formulario ?? ""),
    Score: String(form.scorePorcentagem ?? form.score ?? ""),
    Usuário: String(user),
    Gerência: String(form.gerencia ?? ""),
    Cliente: String(form.cliente ?? ""),
    Local: String(form.local ?? ""),
    Conjunto: String(form.conjunto ?? ""),
    Unidade: String(form.unidade ?? ""),
    "Item de Recorrência": occurrenceName,
    Item: String(item.nome ?? ""),
    Subitem: String(sub.nome ?? ""),
    Resposta: getResposta(sub),
    Comentário: getComentario(sub),
    "Não Conformidades": String(form.naoConforme ?? ""),
    Conformidades: String(form.conforme ?? ""),
  };
};

const appendRowsForItem = (rows: ExportRow[], form: ChecklistReportType, item: ItemChecklist): void => {
  if (item.ocorrenciaMultipla && Array.isArray(item.ocorrencias)) {
    item.ocorrencias.forEach((occurrence: ocorrenciaType) => {
      occurrence.subitens?.forEach((sub: ChecklistSubitem | SubitemChecklist) => {
        if (!sub.checked) {
          return;
        }
        rows.push(buildRow(form, item, sub, String(occurrence.nome ?? "")));
      });
    });
    return;
  }

  item.subitens?.forEach((sub) => {
    if (!sub.checked) {
      return;
    }

    rows.push(buildRow(form, item, sub, findOccurrenceName(item, sub)));
  });
};

const escapeCSVValue = (value: string): string => {
  const safeValue = String(value ?? "");
  if (safeValue.includes(";") || safeValue.includes(",") || safeValue.includes("\n") || safeValue.includes("\r") || safeValue.includes('"')) {
    return `"${safeValue.replace(/"/g, '""')}"`;
  }
  return safeValue;
};

const buildCSV = (rows: ExportRow[]): string => {
  const headerLine = CSV_HEADERS.join(";");
  const dataLines = rows.map((row) => CSV_HEADERS.map((header) => escapeCSVValue(row[header])).join(";"));
  return `\uFEFF${[headerLine, ...dataLines].join("\n")}`;
};

export const downloadResponsesCSV = (form: ChecklistReportType[] | ChecklistReportType): void => {
  const forms = Array.isArray(form) ? form : [form];
  const rows: ExportRow[] = [];

  forms.forEach((currentForm) => {
    currentForm?.itens?.forEach((item: ItemChecklist) => {
      appendRowsForItem(rows, currentForm, item);
    });
  });

  const csvContent = buildCSV(rows);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.href = url;
  link.download = `Respostas-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export default function ModalExportCSV() {
  return null;
}